import './src/styles/global.css'

// Store scroll positions for engagement pages
let savedScrollPositions = {}
let currentScrollY = 0
let scrollTrackingSetup = false

// Set up continuous scroll tracking on initial load
export const onClientEntry = () => {
  if (typeof window !== 'undefined' && !scrollTrackingSetup) {
    scrollTrackingSetup = true

    // Track scroll position continuously
    let scrollTimeout
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        currentScrollY = window.scrollY || window.pageYOffset
      }, 50)
    }, { passive: true })

    // Save scroll position before any navigation
    window.addEventListener('click', (e) => {
      // Check if clicking on a link
      const link = e.target.closest('a')
      if (link && link.href) {
        const currentPath = window.location.pathname
        // Save for both engagement section pages and gallery pages
        // Pattern matches: /engagement/{section} or /engagement/{section}/{galleryId}
        if (currentPath.match(/\/engagement\/([^/]+)(\/[^/]+)?\/?$/)) {
          currentScrollY = window.scrollY || window.pageYOffset
          const normalizedPath = currentPath.replace(/\/$/, '')
          savedScrollPositions[normalizedPath] = currentScrollY
          savedScrollPositions[currentPath] = currentScrollY
        }
      }
    }, true) // Use capture phase
  }
}

// Track previous path for comparison
export const onRouteUpdate = ({ location, prevLocation }) => {
  if (typeof window !== 'undefined' && window.history.state) {
    // Store previous path in history state
    window.history.replaceState(
      { ...window.history.state, prevPath: prevLocation?.pathname },
      ''
    )
  }
}

// Control scroll behavior based on navigation type
export const shouldUpdateScroll = ({
  routerProps: { location },
  prevRouterProps,
  getSavedScrollPosition,
}) => {
  const currentPath = location.pathname
  const prevPath = prevRouterProps?.location?.pathname

  // Pattern for engagement pages: /engagement/{section}/{galleryId} or /cy/engagement/{section}/{galleryId}
  // Pattern for media detail: /engagement/{section}/{galleryId}/{mediaSlug}
  // Pattern for section page: /engagement/{section} or /cy/engagement/{section}
  const currentMatch = currentPath.match(/\/(cy\/)?engagement\/([^/]+)(\/([^/]+))?(\/([^/]+))?/)
  const prevMatch = prevPath?.match(/\/(cy\/)?engagement\/([^/]+)(\/([^/]+))?(\/([^/]+))?/)

  if (currentMatch && prevMatch) {
    const currentLang = currentMatch[1] || ''
    const currentSection = currentMatch[2]
    const currentGallery = currentMatch[4] // Gallery ID if present
    const currentMedia = currentMatch[6] // Media slug if present

    const prevLang = prevMatch[1] || ''
    const prevSection = prevMatch[2]
    const prevGallery = prevMatch[4] // Gallery ID if present
    const prevMedia = prevMatch[6] // Media slug if present

    // Preserve scroll in these cases:
    // 1. Switching galleries within the same engagement section (gallery to gallery)
    // 2. Opening a media item from a gallery (gallery to media detail)
    // 3. Returning from media detail to gallery
    // 4. Navigating from section page to first gallery
    if (currentLang === prevLang && currentSection === prevSection) {
      // Case 1: Switching between galleries in same section
      if (currentGallery && prevGallery && !currentMedia && !prevMedia) {
        return false
      }

      // Case 2: Opening media from gallery - preserve scroll (position saved by click handler)
      if (currentMedia && !prevMedia && currentGallery === prevGallery) {
        return false
      }

      // Case 3: Returning from media to gallery - restore saved position
      if (!currentMedia && prevMedia && currentGallery === prevGallery) {
        const normalizedPath = currentPath.replace(/\/$/, '')
        const savedY = savedScrollPositions[normalizedPath] || savedScrollPositions[currentPath]
        if (savedY !== undefined) {
          // Use setTimeout to ensure DOM is ready
          setTimeout(() => {
            window.scrollTo(0, savedY)
          }, 0)
          return false // Don't let Gatsby handle scroll
        }
        return false
      }

      // Case 4: Navigating from section page (no gallery) to gallery page
      if (currentGallery && !prevGallery && !currentMedia && !prevMedia) {
        return false
      }
    }
  }

  // For all other navigation (different sections, language changes, etc.), use default behavior
  // Try to restore saved scroll position, or scroll to top
  const savedPosition = getSavedScrollPosition(location)
  return savedPosition || [0, 0]
}
